// Bangladeshi mobile phone normalization. Mirrors api/app/core/phone.py's
// algorithm exactly — kept in sync by convention, not shared code, since
// the backend (Python) and this app can't import each other.
//
// Input may arrive as "+8801712345678", "01712345678", "01-712-345-678",
// etc. Anything that doesn't recognizably fit that pattern is rejected
// rather than guessed at — never silently truncate or pad.

export interface PhoneValidationResult {
    valid: boolean;
    normalized?: string;
    error?: string;
}

const INVALID_MESSAGE = "Phone number must start with 01 or +8801";

function normalize(raw: string): string | null {
    // Keep only digits and a leading '+'.
    let cleaned = raw.replace(/[^\d+]/g, "");
    if (cleaned.startsWith("+")) {
        cleaned = "+" + cleaned.slice(1).replace(/\+/g, "");
    } else {
        cleaned = cleaned.replace(/\+/g, "");
    }

    let local: string;
    if (cleaned.startsWith("+8801")) {
        // International format replaces the local leading "0" with "+880"
        // (01712345678 <-> +8801712345678) — has to be added back.
        local = "0" + cleaned.slice(4);
    } else if (cleaned.startsWith("01")) {
        local = cleaned;
    } else {
        return null;
    }

    if (local.length !== 11 || !/^\d+$/.test(local)) {
        return null;
    }
    return local;
}

/**
 * Validate and normalize a Bangladeshi mobile number. Blank input is
 * treated as invalid — callers that allow an optional phone field should
 * check for blank themselves before calling this.
 */
export function validateAndNormalizeBdPhone(raw: string): PhoneValidationResult {
    if (!raw || !raw.trim()) {
        return { valid: false, error: "Phone number is required" };
    }
    const normalized = normalize(raw);
    if (normalized === null) {
        return { valid: false, error: INVALID_MESSAGE };
    }
    return { valid: true, normalized };
}

/**
 * Same normalization, but returns null instead of an error result for
 * anything blank/invalid — for callers that just want a best-effort
 * cleanup (e.g. reformatting an already-stored legacy value) without
 * treating failure as something to surface to a user.
 */
export function tryNormalizeBdPhone(raw: string | null | undefined): string | null {
    if (!raw) return null;
    return normalize(raw);
}
